<script lang="ts">
  import { orgPlus } from "@nrk/origo";
  import { useQueryClient } from "@tanstack/svelte-query";
  import { pageState } from "../../state/page";
  import { productFruitsInstance } from "../../services/productFruitsService";
  import Dialog from "components/Common/Dialog.svelte";
  import { copyNtbImageToMimir } from "services/ntb";
  import { escapeHtml } from "utils/fns";

  import { notify } from "services/notifications";
  import { getCollectionItemsFromQuery } from "state/queries";
  import { createMimirCollectionQuery } from "state/queries/mimir";
  import CreateCollectionDialog from "./CreateCollectionDialog.svelte";
  import SearchableInput from "./SearchableInput.svelte";
  import { addItemToCollection, getCollection } from "services/collections/api";
  import type { Collection } from "bildebanken-model";

  export let selectedImages: string[];
  export let isNtb: boolean = false;
  let ingestingNtbImage = false;

  const collectionsQuery = createMimirCollectionQuery({ sortProperty: "title", sortOrder: "asc" });
  const collections = getCollectionItemsFromQuery(collectionsQuery);

  let selectedCollectionId: string = "";

  const queryClient = useQueryClient();

  async function onCollectionCreated(collection: Collection) {
    await queryClient.invalidateQueries({ queryKey: ["collections"], exact: false });
    selectedCollectionId = collection.id;
  }

  async function onConfirm(): Promise<void> {
    if (!selectedCollectionId) return;
    try {
      for (const itemId of selectedImages) {
        if (isNtb) {
          // Folders only support Mimir items, so NTB items must be ingested first.
          ingestingNtbImage = true;
          try {
            const ingestedNtbItem = await copyNtbImageToMimir(itemId);
            if (!ingestedNtbItem?.id) {
              throw new Error(`NTB-ingest ga ikke gyldig id for item ${itemId}.`);
            }
            await addItemToCollection(selectedCollectionId, ingestedNtbItem.id);
          } finally {
            ingestingNtbImage = false;
          }
        } else {
          await addItemToCollection(selectedCollectionId, itemId);
        }
      }

      const selectedCollection = await getCollection(selectedCollectionId);
      const count = selectedImages.length;
      notify(
        `${count} ${count > 1 ? "bilder" : "bilde"} lagt til i ${escapeHtml(
          selectedCollection.metadata.displayName ?? selectedCollection.name,
        )}. <a style="color: white; margin-left: 0.5rem" href="/images?folderId=${selectedCollectionId}"> Åpne ${escapeHtml(
          selectedCollection.metadata.displayName ?? selectedCollection.name,
        )}</a>`,
        "success",
        7000,
      );

      const source = $pageState.type === "DETAIL" ? "detail" : "search";
      $productFruitsInstance?.api.events.track(`image-added-to-collection-from-${source}`);
    } catch (error) {
      notify(`Kunne ikke legge til i samlingen`, "error", 5000);
      throw error;
    }
  }
</script>

<Dialog
  id="add-to-collection-dialog"
  headerText={"Legg til i samling"}
  confirmText={"Legg til"}
  loadingText={ingestingNtbImage ? "Kopierer fra NTB" : undefined}
  {onConfirm}
  confirmDisabled={!selectedCollectionId}
>
  <h3>Velg destinasjon</h3>
  <p>Velg blant eksisterende samlinger eller opprett en ny for valgte objekt(er)</p>
  {#if $collectionsQuery.isLoading}
    <p>Henter samlinger...</p>
  {:else if $collectionsQuery.isError}
    <p>Kunne ikke hente samlinger.</p>
  {:else}
    <SearchableInput allCollections={$collections} bind:selectedCollectionId />
  {/if}
  <svelte:fragment slot="toolbar-buttons-left">
    <button
      type="button"
      class="org-button org-button--suggestive"
      data-for="create-collection-from-add-to-collection-dialog"
    >
      {@html orgPlus} Opprett ny samling
    </button>
  </svelte:fragment>
</Dialog>

<CreateCollectionDialog
  id="create-collection-from-add-to-collection-dialog"
  onCreated={onCollectionCreated}
/>

<style>
  h3 {
    margin-bottom: calc(-1 * var(--org-small));
  }
</style>
